import { useState } from 'react';
import type { ReactNode } from 'react';

import { useApolloClient } from '@apollo/client';

import type { StaffingTabV2Value } from './StaffingTabsV2';

import useOvertimeGate from '@/hooks/useOvertimeGate';
import type { OvertimeOutcome } from '@/hooks/useOvertimeGate';
import { JOB_HIRE_AND_OFFER_ERROR_MAP } from '@/routes/Agency/Order/InviteAndHireModal';
import { JobActionError, JobsAPI } from '@/services/jobsAPI';
import type { PoolRow } from '@/services/preferredPoolsAPI';
import { JobWorkerStatusEnum } from '@/types/graphql';
import { handleApiFormError } from '@/util/error';
import { trackWorkerHired, trackWorkerInvited } from '@/util/vexo';
import type { StaffingTab } from '@/util/vexo';
import { workerName } from '@/util/worker';

export type StaffAction = 'hire' | 'invite';

export type StaffingErrorEntry = {
  key: string;
  title?: string;
  // Ported verbatim from the original's inline `errors: any[]` shape:
  // `value?.message.replace(...)` only short-circuits to undefined when
  // `value` itself is undefined (the `.replace` call is not itself
  // optionally-chained), so the computed type is honestly `string |
  // undefined`, not `string`.
  msg?: string;
};

export type RehireState = {
  workerId: string;
  worker: Record<string, any>;
  action: StaffAction;
  jobs: Record<string, any>[];
};

export type ConflictWarningState = {
  worker: Record<string, any>;
};

export type UseStaffingActionsArgs = {
  jobs: Record<string, any>[];
  // Ported from WorkerStaffingModel's updateJobAfterHire — same argument
  // order: (jobId, hiredWorkersCount, workerId, jobWorker, workerData).
  // The hook does not own job state; it hands the update to the caller so
  // the future modal (F12-F15) can merge it into its own `jobs` state the
  // same way WorkerStaffingModel's setJobs updater does.
  onHired: (
    jobId: any,
    hiredWorkersCount: any,
    workerId: any,
    jobWorker: any,
    workerData?: any,
  ) => void;
  // Ported from updateJobAfterOffer — (jobId, workerId, jobOffer, workerData).
  onOffered: (
    jobId: any,
    workerId: any,
    jobOffer: any,
    workerData?: any,
  ) => void;
  // Preferred Pools & Dependability v2 analytics (2026-09-02 plan): the
  // modal's currently-resolved tab and the job-skill's pool (if any), so
  // hire/invite trackers can attribute the action to the staffing surface
  // it came from. Optional (rather than required, per the brief) so the
  // existing hook test's `Partial<UseStaffingActionsArgs>` fixtures keep
  // type-checking without every case passing them.
  staffingTab?: StaffingTabV2Value;
  pool?: PoolRow | null;
};

export type UseStaffingActionsResult = {
  staff: (worker: Record<string, any>, action: StaffAction) => Promise<void>;
  // Delta E (2026-07-30): bulk invite for the prototype's "Invite all"
  // button. Sequential per worker so job-state updates and the OT gate
  // behave exactly like single invites; workers whose invite would need a
  // rehire confirmation are SKIPPED and surfaced in errors[] — a bulk
  // action must not chain per-worker confirmation dialogs.
  inviteMany: (workers: Record<string, any>[]) => Promise<void>;
  isStaffing: boolean;
  errors: StaffingErrorEntry[];
  // Ported affordance for the original's Alert onClose={() => setErrors([])}.
  // Not enumerated in the F11 brief's interface list; added because without
  // it the errors array set by `staff` has no way to be dismissed by a
  // consumer. Trivial setState passthrough, no new behavior.
  clearErrors: () => void;
  rehireState: RehireState | null;
  confirmRehire: () => Promise<void>;
  // Ported affordance for the original's ConfirmationModal Cancel / hideModal
  // (denyAction/hideModal both just clear rehireState with no API call).
  // Not enumerated in the brief's interface list; added for the same reason
  // as clearErrors — otherwise a shown rehireState can never be dismissed.
  cancelRehire: () => void;
  conflictWarning: ConflictWarningState | null;
  // Ported affordance for the original's conflict dialog Cancel / Send
  // Invite buttons, collapsed into one resolver: pass `true` to proceed
  // (== "Send Invite"), `false` to dismiss (== "Cancel").
  resolveConflict: (confirm: boolean) => Promise<void>;
  // Necessary addition beyond the brief's enumerated interface: the OT gate
  // (useOvertimeGate) renders a confirmation dialog via its own `modal`
  // JSX. WorkerStaffingModel renders `{overtimeModal}` in its own tree; a
  // consumer of this hook must do the same or the OT confirmation flow has
  // no UI to confirm through. Without exposing it, overtime gating would
  // silently hang (the gate's promise never resolves because the modal
  // that lets the admin confirm/cancel never mounts).
  overtimeModal: ReactNode;
};

// Single (job, worker) hire/invite via REST. Returns:
//  { ok: true, ... }          - success
//  { rehirable: true }        - WORKER_ALREADY_DROPPED_JOB / ALREADY_OFFERED
//  { error: JobActionError }  - any other failure; caller surfaces to errors[]
//
// Ported verbatim from WorkerStaffingModel/index.tsx `submitOne`, plus the
// staffing_tab / pool context (2026-09-02 analytics plan) threaded through
// to both the REST call and the Vexo trackers. `workerId` stays a separate
// parameter (not re-derived from `worker.id`) because confirmRehire's
// `workerId` is the STRINGIFIED id stashed on rehireState — deriving it from
// the raw worker object instead would silently change the id shape sent to
// the API/tracker on a rehire retry. `worker` is only the full row object,
// used for its dependability tier. `staffingTab`/`pool` are the hook's own
// args, passed in by each caller (staffOne, confirmRehire) since this
// function lives outside the hook's closure.
const submitOne = async (
  job: Record<string, any>,
  workerId: any,
  worker: Record<string, any>,
  action: string,
  force: boolean,
  otOutcome: OvertimeOutcome | null,
  staffingTab?: StaffingTabV2Value,
  pool?: PoolRow | null,
): Promise<{
  ok?: boolean;
  rehirable?: boolean;
  error?: JobActionError;
  jobWorker?: any;
  jobOffer?: any;
  hiredWorkersCount?: number;
}> => {
  try {
    if (action === 'invite') {
      if (isPastSchedule(job)) return { ok: true };
      const resp = await JobsAPI.createOffer(job.id, {
        workerId,
        forceInvite: force,
        staffingTab,
      });
      // Vexo `worker_invited`. Force-invites (rehire) skip the event to
      // match the OT-gate force-skip convention.
      if (!force) {
        trackWorkerInvited({
          job_id: job.id,
          worker_id: workerId,
          worker_current_hours: otOutcome?.worker_current_hours ?? null,
          overtime_triggered: otOutcome?.triggered ?? false,
          overtime_hours: otOutcome?.overtime_hours ?? null,
          staffing_tab: staffingTab,
          pool_id: pool?.id ?? null,
          // No pool-membership flag exists on the staffing-modal worker row
          // today (see report) — null per the D2/decisions note rather than
          // an extra network call.
          worker_in_pool: null,
          dependability_tier: worker?.dependabilityTier ?? null,
        });
      }
      return { ok: true, jobOffer: resp.data.job_offer };
    }
    const resp = await JobsAPI.hireWorker(job.id, {
      workerId,
      forceHire: force,
      staffingTab,
    });
    if (!force) {
      trackWorkerHired({
        job_id: job.id,
        worker_id: workerId,
        worker_current_hours: otOutcome?.worker_current_hours ?? null,
        overtime_triggered: otOutcome?.triggered ?? false,
        overtime_hours: otOutcome?.overtime_hours ?? null,
        staffing_tab: staffingTab,
        pool_id: pool?.id ?? null,
        worker_in_pool: null,
        dependability_tier: worker?.dependabilityTier ?? null,
      });
    }
    return {
      ok: true,
      jobWorker: resp.data.job_worker,
      hiredWorkersCount: resp.data.job.hired_workers_count,
    };
  } catch (err) {
    if (
      err instanceof JobActionError &&
      (err.code === 'WORKER_ALREADY_DROPPED_JOB' ||
        err.code === 'ALREADY_OFFERED')
    ) {
      return { rehirable: true };
    }
    if (err instanceof JobActionError) {
      return { error: err };
    }
    // Unknown error — surface as a generic API failure.
    return {
      error: new JobActionError(
        'SERVER_ERROR',
        'Something went wrong. Please try again.',
        500,
        { success: false, errors: ['SERVER_ERROR'] },
      ),
    };
  }
};

const isPastSchedule = (schedule: Record<string, any>) =>
  new Date(schedule.firstShiftStartAt).getTime() < new Date().getTime();

export const useStaffingActions = ({
  jobs,
  onHired,
  onOffered,
  staffingTab,
  pool,
}: UseStaffingActionsArgs): UseStaffingActionsResult => {
  const apolloClient = useApolloClient();
  const { check: checkOvertime, modal: overtimeModal } = useOvertimeGate();

  const [isStaffing, setIsStaffing] = useState(false);
  const [errors, setErrors] = useState<StaffingErrorEntry[]>([]);
  const [rehireState, setRehireState] = useState<RehireState | null>(null);
  const [conflictWarning, setConflictWarning] =
    useState<ConflictWarningState | null>(null);

  // Refetch the active GetOrder / GetJob queries so the parent page reflects
  // the new hire/offer. Apollo no-ops the names that aren't active.
  const refetchOpenQueries = async () => {
    try {
      await apolloClient.refetchQueries({ include: ['GetOrder', 'GetJob'] });
    } catch {
      // Cache stays stale until next navigation; not worth blocking on.
    }
  };

  // Ported verbatim from WorkerStaffingModel/index.tsx `staffingHandler`,
  // minus the `mode === 'remove'` UI-toggle guard (that guard belongs to the
  // row-button "add"/"remove" affordance in StaffingWorkerProfileRow, which
  // is out of scope for F11 — see report) and minus the GA `logStaffingEvent`
  // call (which needs the search-tab UI state that doesn't exist in this
  // hook — see report).
  // The per-(worker, action) job loop, extracted verbatim from runStaffing
  // so inviteMany (delta E) can reuse it without touching the
  // isStaffing/errors/rehireState wrappers. Returns the jobs that need a
  // rehire confirmation; the caller decides what to do with them.
  // GRAVY-10437: failures go through `reportError` so a bulk caller can
  // collect them synchronously per worker; the single-action default keeps
  // the ported "replace errors[] with one entry" behaviour.
  const staffOne = async (
    worker: Record<string, any>,
    action: StaffAction,
    reportError: (entry: StaffingErrorEntry) => void = (entry) =>
      setErrors([entry]),
  ): Promise<Record<string, any>[]> => {
    const workerId = worker.id;
    const checkProperty = action === 'invite' ? 'jobOffers' : 'jobWorkers';
    const rehireJobs: Record<string, any>[] = [];

    const selectedIds = jobs
      .filter((j) => j.hiredWorkersCount < j.quantity)
      .map((j) => j.id);

    for (const job of jobs) {
      if (
        !selectedIds.includes(job.id) ||
        job[checkProperty].some(
          (w) =>
            w.worker.id === workerId.toString() &&
            w.status !== JobWorkerStatusEnum.DISMISSED &&
            w.status !== JobWorkerStatusEnum.DROPPED &&
            !w.rejectedAt,
        )
      )
        continue;

      // Per-job OT gate. Cancel skips that job and the loop continues.
      const otOutcome = await new Promise<OvertimeOutcome | null>((resolve) => {
        checkOvertime(
          job.id,
          worker,
          action === 'invite' ? 'invite' : 'hire',
          (outcome) => resolve(outcome),
          { onCancel: () => resolve(null) },
        );
      });
      if (!otOutcome) continue;

      const result = await submitOne(
        job,
        workerId,
        worker,
        action,
        false,
        otOutcome,
        staffingTab,
        pool,
      );
      if (result.rehirable) {
        rehireJobs.push(job);
      } else if (result.ok) {
        if (action === 'invite' && result.jobOffer) {
          onOffered(job.id, workerId, result.jobOffer, worker);
        } else if (result.jobWorker) {
          onHired(
            job.id,
            result.hiredWorkersCount,
            workerId,
            result.jobWorker,
            worker,
          );
        }
      } else if (result.error) {
        handleApiFormError(
          result.error,
          {
            errorMap: JOB_HIRE_AND_OFFER_ERROR_MAP,
            // Ported verbatim: each failure REPLACES errors with a single-
            // item array rather than appending. In a multi-job loop only the
            // last failure survives. This looks like a bug in the original
            // but is preserved as-is per instructions — see report.
            setFormError: (key, value) =>
              reportError({
                key,
                title: value?.title,
                msg: value?.message.replace('{0}', workerName(worker)),
              }),
          },
          workerName(worker),
        );
      }
    }

    return rehireJobs;
  };

  const runStaffing = async (
    worker: Record<string, any>,
    action: StaffAction,
  ) => {
    setErrors([]);
    setIsStaffing(true);

    const rehireJobs = await staffOne(worker, action);

    if (rehireJobs.length > 0) {
      setRehireState({
        workerId: String(worker.id),
        worker,
        action,
        jobs: rehireJobs,
      });
    } else {
      await refetchOpenQueries();
    }

    setIsStaffing(false);
  };

  // Delta E: see UseStaffingActionsResult. The caller pre-filters to
  // eligible workers (invitable, not already invited/hired, no conflicts) —
  // this loop only adds the rehire-skip that can't be known up front.
  const inviteMany = async (workers: Record<string, any>[]) => {
    setErrors([]);
    setIsStaffing(true);
    const skipped: string[] = [];
    // staffOne's failure path REPLACES errors[] with a single entry (ported
    // behaviour, deliberate for a single-worker action). Looping it over N
    // workers meant a bulk invite where 30 of 50 failed surfaced exactly one
    // message. Collect each worker's failure as the loop goes and restore the
    // accumulated set at the end, so "Invite all" reports everything it hit.
    // GRAVY-10437: every collected entry is keyed AND prefixed by the worker
    // it belongs to — a generic "Server error" with no name told the admin
    // nothing about who was actually invited.
    const collected: StaffingErrorEntry[] = [];
    let failed = 0;

    for (const worker of workers) {
      const name = workerName(worker);
      let workerFailed = false;
      const rehireJobs = await staffOne(worker, 'invite', (entry) => {
        workerFailed = true;
        const key = `${entry.key}:${worker.id}`;
        if (collected.some((e) => e.key === key)) return;
        const msg = entry.msg ?? '';
        collected.push({
          ...entry,
          key,
          msg: msg.includes(name) ? msg : `${name} — ${msg}`,
        });
      });
      if (rehireJobs.length > 0) skipped.push(name);
      else if (workerFailed) failed += 1;
    }

    const invited = workers.length - skipped.length - failed;
    const summary: StaffingErrorEntry[] =
      skipped.length > 0 || failed > 0
        ? [
            {
              key: 'INVITE_ALL_SUMMARY',
              title: 'Invite all finished with problems',
              msg: `${invited} invited, ${skipped.length} skipped, ${failed} failed. Workers marked Invited were invited successfully.`,
            },
          ]
        : [];
    const skipSummary: StaffingErrorEntry[] =
      skipped.length > 0
        ? [
            {
              key: 'INVITE_ALL_SKIPPED',
              title: 'Some workers were not invited',
              msg: `${skipped.join(', ')} previously declined an invitation or dropped these jobs — invite them individually to confirm.`,
            },
          ]
        : [];
    setErrors([...summary, ...skipSummary, ...collected]);

    await refetchOpenQueries();
    setIsStaffing(false);
  };

  // Ported from WorkerStaffingModel's `handleInviteWithConflictCheck` +
  // `staffingHandler` entry point. Conflict checking only ever applied to
  // the invite path in the original (hire's button wired straight to
  // staffingHandler with no conflict check), so that asymmetry is preserved.
  const staff = async (worker: Record<string, any>, action: StaffAction) => {
    if (action === 'invite' && worker.hasConflicts) {
      setConflictWarning({ worker });
      return;
    }
    await runStaffing(worker, action);
  };

  // Ported from WorkerStaffingModel's conflict dialog Cancel / "Send Invite"
  // buttons, collapsed into one resolver.
  const resolveConflict = async (confirm: boolean) => {
    const warning = conflictWarning;
    setConflictWarning(null);
    if (confirm && warning) {
      await runStaffing(warning.worker, 'invite');
    }
  };

  // Ported verbatim from WorkerStaffingModel/index.tsx `handleRehireConfirm`.
  // Note: unlike runStaffing, failures here (result.error) are silently
  // dropped — no errors[] entry is set. That is the original's behavior,
  // preserved as-is; see report.
  const confirmRehire = async () => {
    if (!rehireState) return;
    setIsStaffing(true);
    const { workerId, action, worker } = rehireState;
    for (const job of rehireState.jobs) {
      const result = await submitOne(
        job,
        workerId,
        worker,
        action,
        true,
        null,
        staffingTab,
        pool,
      );
      if (result.ok) {
        if (action === 'invite' && result.jobOffer) {
          onOffered(job.id, workerId, result.jobOffer, worker);
        } else if (result.jobWorker) {
          onHired(
            job.id,
            result.hiredWorkersCount,
            workerId,
            result.jobWorker,
            worker,
          );
        }
      }
    }
    await refetchOpenQueries();
    setRehireState(null);
    setIsStaffing(false);
  };

  const cancelRehire = () => setRehireState(null);
  const clearErrors = () => setErrors([]);

  return {
    staff,
    inviteMany,
    isStaffing,
    errors,
    clearErrors,
    rehireState,
    confirmRehire,
    cancelRehire,
    conflictWarning,
    resolveConflict,
    overtimeModal,
  };
};

export default useStaffingActions;
